import { createContext, useContext, useEffect, useRef } from "react";
import useNotifications from "../hook/useNotifications";
import { toast } from "react-hot-toast";


const NotificationContext = createContext();

// 🔔 Function to play the sound using the public path
const playNotificationSound = () => {
  // The path starts from the root of the 'public' directory
  const soundPath = "/assets/alert.wav";

  try {
    const audio = new Audio(soundPath);
    // Reduce the volume to prevent being too loud
    // audio.volume = 0.5;
    audio.play().catch(error => {
      console.warn("Autoplay was prevented. User interaction required to play sound.", error);
    });
  } catch (error) {
    console.error("Error playing notification sound:", error);
  }
};


export function NotificationProvider({ children, userId, userRole }) {
  const { notifications, isLoaded } = useNotifications(userId, userRole);

  // 💡 Change 1: Use a state to track if the initial load is complete
  const isInitialLoadRef = useRef(true);
  const prevIdsRef = useRef(new Set()); // stores old notification IDs


  useEffect(() => {
    if (!isLoaded) return;
    if (!Array.isArray(notifications)) return;

    const currentIds = new Set(notifications.map((n) => n.id));

    // 1. Handle Initial Load
    // 💡 Change 2: Skip toast firing on initial load, but update prevIdsRef
    if (isInitialLoadRef.current) {
      // console.log("Initial notification load complete. Skipping toasts.");
      prevIdsRef.current = currentIds;
      isInitialLoadRef.current = false;
      return;
    }

    // 2. Handle subsequent updates (New notifications)
    const prevIds = prevIdsRef.current;

    notifications.forEach((n) => {
      // Check if the notification ID is NOT in the previous list.
      const isNewNotification = !prevIds.has(n.id);

      if (isNewNotification && !n.isRead) {
        // Play sound for new unread notification
        playNotificationSound();
        // 🔥 FIRE TOAST ONLY FOR TRULY NEW UNREAD NOTIFICATIONS
        toast(
          (t) => (
            <div style={{ position: "relative", paddingBottom: "6px" }}>
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{
                  position: "absolute",
                  top: "-10px",
                  right: "-14px",
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                ✖
              </button>

              <div>
                <strong>{n.title}</strong> — {n.patient_name}
                <span style={{ color: "lightgreen" }}> (New)</span>
                <span>{n.packages && Array.isArray(n.packages) ? n.packages.join(", ") : " "} </span>
                <span> &nbsp; at {n.center_name}</span>
              </div>

              <div
                style={{
                  width: "100%",
                  height: "4px",
                  background: "rgba(255,255,255,0.3)",
                  position: "absolute",
                  bottom: "0",
                  left: "0",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "lightgreen",
                    animation: "progress 4s linear forwards",
                  }}
                />
              </div>
            </div>
          ),
          {
            duration: 4000,
            position: "bottom-right",
            style: {
              background: "#333",
              color: "#fff",
              zIndex: "99999",
              minWidth: "280px",
              padding: "10px 10px",
              borderRadius: "8px",
            },
          }
        );
      }
    });

    // 3. Update previous IDs for the next run
    prevIdsRef.current = currentIds;

  }, [notifications, isLoaded]); // Dependencies remain the same

  return (
    <NotificationContext.Provider value={{ notifications, userId }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
}